import { Button, Flex } from "antd";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import type { IPublicCourseResponse } from "../../../app/features/courses/courses.types";
import {
  useAddWishlistMutation,
  useGetCheckWishlistQuery,
  useRemoveWishlistMutation,
} from "../../../app/features/wishlist/wishlist.api";
import SaveIcon from "../../../assets/icons/SaveIcon";
import SaveIconActive from "../../../assets/icons/SaveIconActive";
import ShareIcon from "../../../assets/icons/ShareIcon";
import useAuth from "../../../hooks/useAuth";
import { toast } from "../../../lib/toast";
import { commaNumber, enToBnNumber } from "../../../utils/number.utils";
import ShareSocialMedia from "../../ShareSocialMedia";
import { getHighlightList } from "./CourseOverview";
import TechServiceCardLoading from "./TechServiceCardLoading";

type Props = {
  moreButtonText?: string;
  onMoreButton?: (id: string | number) => void;
  course?: IPublicCourseResponse;
  isLoading?: boolean;
  moreButtonLoading?: boolean;
  moreButtonDisabled?: boolean;
  onWish?: (type: "wish" | "default", id: number | string) => void;
  isWish?: boolean;
};
const TechServiceCard = ({
  moreButtonText = "কোর্সটি কিনুন",
  onMoreButton,
  course,
  isLoading,
  moreButtonLoading,
  moreButtonDisabled,
  onWish,
  isWish,
}: Props) => {
  const shareUrl = `${window.location.origin}/courses/${course?.id}`;
  const courseUuid = course?.uuid;
  const requirementList = getHighlightList(
    course?.CourseHighlight,
    "REQUIREMENT",
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [addWishlist, { isLoading: addLoading }] = useAddWishlistMutation();
  const [removeWishlist, { isLoading: removeLoading }] =
    useRemoveWishlistMutation();

  // Check if course is in wishlist (only if authenticated and has UUID)
  const { data: checkWishlist } = useGetCheckWishlistQuery(
    { courseUuid: courseUuid! },
    { skip: !courseUuid || !isAuthenticated },
  );

  const isWishlisted =
    courseUuid && isAuthenticated ? checkWishlist?.exists : isWish;

  const isShowOfferPrice =
    Number(course?.price || 0) > 0 &&
    Number(course?.price || 0) !== Number(course?.offerPrice || 0);

  const handleWish = async () => {
    if (!isAuthenticated) {
      if (location.pathname !== "/login") {
        navigate("/login", { state: { from: location } });
      }
      return;
    }

    if (!courseUuid) {
      // Fallback to prop handler if no UUID (e.g. mock data)
      if (onWish && course?.id)
        onWish(isWishlisted ? "wish" : "default", course?.id);
      return;
    }

    if (addLoading || removeLoading) {
      return;
    }

    try {
      if (isWishlisted) {
        // message.loading("উইশলিস্ট থেকে সরানো হচ্ছে...");
        // Assuming simple toast usage based on lib definition.
        await removeWishlist({ courseUuid }).unwrap();
        toast("success", "উইশলিস্ট থেকে সফলভাবে সরানো হয়েছে");
      } else {
        // message.loading("উইশলিস্টে যোগ করা হচ্ছে...");
        await addWishlist({
          courseUuid,
          note: "Added from course list",
        }).unwrap();
        toast("success", "উইশলিস্টে সফলভাবে যোগ করা হয়েছে");
      }
    } catch (error) {
      toast("error", "উইশলিস্ট আপডেট করতে ব্যর্থ হয়েছে");
    }
  };

  if (isLoading) {
    return <TechServiceCardLoading />;
  }

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden relative z-20 lg:-mt-64">
      {/* Hero Banner */}
      <img
        className="h-56 w-full object-cover rounded-t-xl"
        src={course?.thumbnailUrl || "/images/demo/course/course-photo.svg"}
      />

      {/* Content Section */}
      <div className="p-4 space-y-4 bg-theme-slate-25">
        {/* Price and Actions */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={8}>
            <h6 className="text-red-500 text-2xl font-semibold">
              ৳{enToBnNumber(commaNumber(course?.offerPrice))}
            </h6>
            {isShowOfferPrice && (
              <span className="text-theme-slate-400 text-sm font-medium line-through">
                ৳{enToBnNumber(commaNumber(course?.price))}
              </span>
            )}
          </Flex>
          <Flex
            align="center"
            gap={16}
            className="relative text-theme-slate-500 -top-1"
          >
            <Flex
              align="center"
              justify="center"
              className="size-6"
              gap={0}
              onClick={handleWish}
            >
              {isWishlisted ? (
                <SaveIconActive className="cursor-pointer" />
              ) : (
                <SaveIcon className="cursor-pointer" />
              )}
            </Flex>
            <ShareSocialMedia url={shareUrl}>
              <ShareIcon />
            </ShareSocialMedia>
          </Flex>
        </Flex>

        {/* Description */}
        <p className="text-theme-zinc-700 text-sm font-normal">
          {course?.description}
        </p>

        {/* Bullet Points */}
        <ul className="list-disc list-inside space-y-2 mb-6 pl-2">
          {requirementList.map((item, index) => (
            <li key={index} className="text-sm text-theme-zinc-700">
              {item}
            </li>
          ))}
        </ul>

        {/* Tags */}
        <Flex wrap gap={12} className="mb-6">
          {course?.CourseTag?.map((item) => (
            <Item key={item?.id}>{item?.Tag?.name}</Item>
          ))}
        </Flex>

        {/* CTA Button */}
        <Button
          variant="outlined"
          type="primary"
          size="large"
          block
          className="rounded-full"
          onClick={() => onMoreButton && onMoreButton("123")}
          loading={moreButtonLoading}
          disabled={moreButtonDisabled}
        >
          {moreButtonText}
        </Button>
      </div>
    </div>
  );
};

export default TechServiceCard;

type IItem = {
  children: ReactNode;
};
const Item = ({ children }: IItem) => (
  <span className="px-1.5 py-1 bg-theme-slate-100 rounded text-theme-zinc-700 text-xs font-normal">
    {children}
  </span>
);
